import { cloneCanvas } from "./domain/canvas";
import type {
  CanvasGeometry,
  CanvasNode,
  CanvasViewState,
  DomainCanvas,
  JsonValue,
} from "./domain/types";

export type CanvasValidationCode =
  | "invalid-document"
  | "invalid-id"
  | "duplicate-node-id"
  | "duplicate-edge-id"
  | "invalid-geometry"
  | "invalid-json"
  | "invalid-reference"
  | "dangling-edge"
  | "invalid-frame-parent"
  | "cyclic-frame"
  | "invalid-reading-order"
  | "invalid-view";

export interface CanvasValidationIssue {
  code: CanvasValidationCode;
  path: string;
  message: string;
  entityId?: string;
}

export class CanvasValidationError extends TypeError {
  readonly code = "CANVAS_VALIDATION_FAILED";
  readonly issues: readonly CanvasValidationIssue[];

  constructor(issues: readonly CanvasValidationIssue[]) {
    super(
      issues.length === 1
        ? issues[0]!.message
        : `Canvas validation failed with ${issues.length} issues`,
    );
    this.name = "CanvasValidationError";
    this.issues = issues;
  }
}

function issue(
  issues: CanvasValidationIssue[],
  code: CanvasValidationCode,
  path: string,
  message: string,
  entityId?: string,
): void {
  issues.push({
    code,
    path,
    message,
    ...(entityId === undefined ? {} : { entityId }),
  });
}

export function isCanvasJsonValue(
  value: unknown,
  seen = new Set<object>(),
): value is JsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "object") return false;
  if (seen.has(value)) return false;
  seen.add(value);
  if (Array.isArray(value)) {
    const valid = value.every((entry) => isCanvasJsonValue(entry, seen));
    seen.delete(value);
    return valid;
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    seen.delete(value);
    return false;
  }
  const valid = Object.values(value).every((entry) =>
    isCanvasJsonValue(entry, seen),
  );
  seen.delete(value);
  return valid;
}

function validateGeometry(
  geometry: CanvasGeometry,
  path: string,
  entityId: string,
  issues: CanvasValidationIssue[],
): void {
  for (const key of ["x", "y", "width", "height", "rotation"] as const) {
    if (!Number.isFinite(geometry[key])) {
      issue(
        issues,
        "invalid-geometry",
        `${path}.${key}`,
        `${path}.${key} must be a finite number`,
        entityId,
      );
    }
  }
  if (Number.isFinite(geometry.width) && geometry.width <= 0) {
    issue(
      issues,
      "invalid-geometry",
      `${path}.width`,
      `${path}.width must be greater than zero`,
      entityId,
    );
  }
  if (Number.isFinite(geometry.height) && geometry.height <= 0) {
    issue(
      issues,
      "invalid-geometry",
      `${path}.height`,
      `${path}.height must be greater than zero`,
      entityId,
    );
  }
  if (typeof geometry.zIndex !== "string" || geometry.zIndex.length === 0) {
    issue(
      issues,
      "invalid-geometry",
      `${path}.zIndex`,
      `${path}.zIndex must be a non-empty string`,
      entityId,
    );
  }
}

function validateFrameGraph(
  nodes: readonly CanvasNode[],
  issues: CanvasValidationIssue[],
): void {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  for (const [index, node] of nodes.entries()) {
    if (node.parentFrameId === undefined) continue;
    const parent = byId.get(node.parentFrameId);
    if (parent?.type !== "frame") {
      issue(
        issues,
        "invalid-frame-parent",
        `canvas.nodes[${index}].parentFrameId`,
        `Node ${node.id} parentFrameId must reference a Frame node`,
        node.id,
      );
      continue;
    }
    const path = new Set<string>([node.id]);
    let cursor: CanvasNode | undefined = parent;
    while (cursor !== undefined) {
      if (path.has(cursor.id)) {
        issue(
          issues,
          "cyclic-frame",
          `canvas.nodes[${index}].parentFrameId`,
          `Frame membership for node ${node.id} contains a cycle`,
          node.id,
        );
        break;
      }
      path.add(cursor.id);
      cursor =
        cursor.parentFrameId === undefined
          ? undefined
          : byId.get(cursor.parentFrameId);
    }
  }
}

export function collectCanvasValidationIssues(
  canvas: DomainCanvas,
): CanvasValidationIssue[] {
  const issues: CanvasValidationIssue[] = [];
  if (!["freeform", "mindmap", "research", "project"].includes(canvas.mode)) {
    issue(
      issues,
      "invalid-document",
      "canvas.mode",
      `Unsupported canvas mode: ${String(canvas.mode)}`,
    );
  }
  if (!Number.isInteger(canvas.schemaVersion) || canvas.schemaVersion < 1) {
    issue(
      issues,
      "invalid-document",
      "canvas.schemaVersion",
      "canvas.schemaVersion must be a positive integer",
    );
  }
  if (!isCanvasJsonValue(canvas.settings)) {
    issue(
      issues,
      "invalid-json",
      "canvas.settings",
      "canvas.settings must contain only finite JSON values",
    );
  }

  const nodeIds = new Set<string>();
  for (const [index, node] of canvas.nodes.entries()) {
    const path = `canvas.nodes[${index}]`;
    if (typeof node.id !== "string" || node.id.length === 0) {
      issue(issues, "invalid-id", `${path}.id`, `${path}.id must be non-empty`);
    } else if (nodeIds.has(node.id)) {
      issue(
        issues,
        "duplicate-node-id",
        `${path}.id`,
        `Duplicate node id: ${node.id}`,
        node.id,
      );
    }
    nodeIds.add(node.id);
    if (typeof node.type !== "string" || node.type.length === 0) {
      issue(
        issues,
        "invalid-document",
        `${path}.type`,
        `${path}.type must be a non-empty string`,
        node.id,
      );
    }
    validateGeometry(node.geometry, `${path}.geometry`, node.id, issues);
    if (!isCanvasJsonValue(node.props)) {
      issue(
        issues,
        "invalid-json",
        `${path}.props`,
        `${path}.props must contain only finite JSON values`,
        node.id,
      );
    }
    if (
      node.reference !== undefined &&
      (typeof node.reference.id !== "string" ||
        node.reference.id.length === 0 ||
        !["item", "block", "attachment"].includes(node.reference.kind))
    ) {
      issue(
        issues,
        "invalid-reference",
        `${path}.reference`,
        `${path}.reference is not a valid reference target`,
        node.id,
      );
    }
    if (
      node.readingOrder !== undefined &&
      (!Number.isSafeInteger(node.readingOrder) || node.readingOrder < 0)
    ) {
      issue(
        issues,
        "invalid-reading-order",
        `${path}.readingOrder`,
        `${path}.readingOrder must be a non-negative safe integer`,
        node.id,
      );
    }
  }

  const edgeIds = new Set<string>();
  for (const [index, edge] of canvas.edges.entries()) {
    const path = `canvas.edges[${index}]`;
    if (typeof edge.id !== "string" || edge.id.length === 0) {
      issue(issues, "invalid-id", `${path}.id`, `${path}.id must be non-empty`);
    } else if (edgeIds.has(edge.id)) {
      issue(
        issues,
        "duplicate-edge-id",
        `${path}.id`,
        `Duplicate edge id: ${edge.id}`,
        edge.id,
      );
    }
    edgeIds.add(edge.id);
    if (!nodeIds.has(edge.sourceNodeId) || !nodeIds.has(edge.targetNodeId)) {
      issue(
        issues,
        "dangling-edge",
        path,
        `Edge ${edge.id} must connect two existing nodes`,
        edge.id,
      );
    }
    if (!isCanvasJsonValue(edge.props)) {
      issue(
        issues,
        "invalid-json",
        `${path}.props`,
        `${path}.props must contain only finite JSON values`,
        edge.id,
      );
    }
    if (
      edge.props.width !== undefined &&
      (typeof edge.props.width !== "number" ||
        !Number.isFinite(edge.props.width) ||
        edge.props.width <= 0)
    ) {
      issue(
        issues,
        "invalid-document",
        `${path}.props.width`,
        `${path}.props.width must be a positive finite number`,
        edge.id,
      );
    }
    if (
      edge.props.dash !== undefined &&
      edge.props.dash !== "solid" &&
      edge.props.dash !== "dashed"
    ) {
      issue(
        issues,
        "invalid-document",
        `${path}.props.dash`,
        `${path}.props.dash must be solid or dashed`,
        edge.id,
      );
    }
    for (const arrow of ["arrowStart", "arrowEnd"] as const) {
      if (
        edge.props[arrow] !== undefined &&
        typeof edge.props[arrow] !== "boolean"
      ) {
        issue(
          issues,
          "invalid-document",
          `${path}.props.${arrow}`,
          `${path}.props.${arrow} must be a boolean`,
          edge.id,
        );
      }
    }
  }
  validateFrameGraph(canvas.nodes, issues);
  return issues;
}

export function assertValidCanvas(canvas: DomainCanvas): DomainCanvas {
  const issues = collectCanvasValidationIssues(canvas);
  if (issues.length > 0) throw new CanvasValidationError(issues);
  return cloneCanvas(canvas);
}

export function sanitizeCanvasView(
  view: Partial<CanvasViewState> = {},
): CanvasViewState {
  const finiteOr = (value: number | undefined, fallback: number): number =>
    value === undefined || !Number.isFinite(value) ? fallback : value;
  return {
    panX: finiteOr(view.panX, 0),
    panY: finiteOr(view.panY, 0),
    zoom: Math.min(8, Math.max(0.1, finiteOr(view.zoom, 1))),
    viewportWidth: Math.max(0, finiteOr(view.viewportWidth, 0)),
    viewportHeight: Math.max(0, finiteOr(view.viewportHeight, 0)),
  };
}
